import {
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import clsx from 'clsx';
import useDebounce from 'common/hooks/useDebounce';
import {
  useGetCustomerDetails,
  useGetCustomerPayments
} from 'common/queries-and-mutations/admin/customer';
import Badge from 'components/Badge';
import { Icon, Icons } from 'components/Icon';
import { Loader } from 'components/Loader';
import Breadcrumb from 'components/Navigation/Breadcrumb';
import AdminTableProperties from 'components/TableWidget/AdminTableProperties';
import Paginator from 'components/TableWidget/Paginator';
import { format } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Payment } from 'service/dto/customer.dto';
import { ListOrder, PaginatedListMeta } from 'types/general.type';
import { formatToMoney } from 'utils';

export default function PaymentList() {
  const { customerId } = useParams();

  const [urlParams, setUrlParams] = useSearchParams();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [isExecutingSearch, setIsExecutingSearch] = useState<boolean>(true);
  const searchQueryString = urlParams.get('search_query') ?? '';
  const currentPage = urlParams.get('page') ?? '1';
  const numberOfItemsPerPage = urlParams.get('items_per_page') || 10;
  const debounceSearch = useDebounce(searchQueryString, 1300);

  const { data: user } = useGetCustomerDetails({
    customerId: +customerId!
  });

  const getPaginationParams = useCallback(() => {
    return {
      page: +currentPage,
      take: +numberOfItemsPerPage,
      order: ListOrder.ASC
    };
  }, [currentPage, numberOfItemsPerPage]);

  const computedWhereOptions = useCallback(() => {
    if (searchQueryString.trim().length > 2) {
      return {
        search_query: searchQueryString,
        email: user?.email
      };
    }
    return {
      search_query:
        searchQueryString.trim().length > 3 ? searchQueryString : '',
      email: user?.email
    };
  }, [user?.email, searchQueryString]);

  const { data: payments, isFetching } = useGetCustomerPayments({
    whereOptions: computedWhereOptions(),
    paginationOptions: getPaginationParams(),
    enabled: isExecutingSearch
  });

  useEffect(() => {
    if (!isFetching && isExecutingSearch) {
      setIsExecutingSearch(false);
    }
  }, [isExecutingSearch, isFetching]);

  useEffect(() => {
    if (debounceSearch.length > 3) {
      setIsExecutingSearch(true);
    }
  }, [debounceSearch.length]);

  const columnHelper = createColumnHelper<Payment>();

  const columns = [
    columnHelper.accessor(row => row.reference, {
      id: 'reference',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">
          Transaction ID
        </span>
      )
    }),
    columnHelper.accessor(row => row?.planType, {
      id: 'planType',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Product</span>
      )
    }),
    columnHelper.accessor(row => row.amount, {
      id: 'amount',
      cell: info => (
        <span className="text-xs font-normal">
          {formatToMoney(info.getValue() / 100)}
        </span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">
          Amount Paid
        </span>
      )
    }),
    columnHelper.accessor(row => row.createdAt, {
      id: 'createdAt',
      cell: info => (
        <span className="text-xs font-normal">
          {format(new Date(info.getValue()), 'dd MMM yyyy, H:M')}
        </span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Date</span>
      )
    }),
    columnHelper.accessor(row => row.paymentMethod, {
      id: 'paymentMethod',
      cell: info => (
        <span className="text-xs font-normal">{info.getValue()}</span>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">
          Payment Channel
        </span>
      )
    }),
    columnHelper.accessor(row => row.status, {
      id: 'isActive',
      cell: info => (
        <Badge
          size="sm"
          title={info.getValue().toUpperCase()}
          type={info.getValue() === 'success' ? 'success' : 'danger'}
          className="text-white"
        />
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Status</span>
      )
    }),
    columnHelper.accessor(row => row.id, {
      id: 'id',
      cell: info => (
        <div>
          <Link
            to={`/admin/customers/payment-details/${customerId}/${info.getValue()}`}
            className="rounded-md bg-primary-900 p-2 px-5 text-white underline"
          >
            View
          </Link>
        </div>
      ),
      header: () => (
        <span className="text-xs font-semibold text-[#1F4173]">Action</span>
      )
    })
  ];

  const data = useMemo<Payment[]>(() => {
    const normalizedResult: any[] = (payments?.data ?? []).map(item => ({
      id: item.id,
      email: item.email,
      reference: item.reference,
      planType: item.planType,
      amount: item.amount,
      paymentMethod: item.paymentMethod,
      status: item.status,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      deletedAt: item.deletedAt
    }));
    return normalizedResult;
  }, [payments]);

  const listMetaData = (payments?.meta ?? {}) as PaginatedListMeta;

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting
    },
    initialState: {
      pagination: {
        pageSize: +numberOfItemsPerPage
      }
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  });

  const handleChange = (e: React.ChangeEvent<any>) => {
    const { name, value } = e.target;
    if (value.trim() === '') {
      urlParams.delete(name);
      setUrlParams(urlParams);
    }
    if (value.trim()) {
      urlParams.set(name, value);
      setUrlParams(urlParams);
    }
  };

  const goToNextPage = () => {
    const nextPageValue = currentPage ? `${+currentPage + 1}` : '2';
    urlParams.set('page', nextPageValue);
    setUrlParams(urlParams);
  };

  const goToPreviousPage = () => {
    if (currentPage && +currentPage > 1) {
      const previousPageValue = `${+currentPage - 1}`;
      urlParams.set('page', previousPageValue);
      setUrlParams(urlParams);
    }
  };

  if (isFetching) {
    return (
      <div className="mt-40 flex flex-col items-center justify-center">
        <Loader color={'#FF6606'} height={50} width={50} />
      </div>
    );
  }

  return (
    <div>
      <div className="mt-4 flex items-center justify-between">
        <Breadcrumb title="Payment History" />

        <Link
          to={`/admin/customers/details/${customerId}`}
          className="flex items-center gap-2"
        >
          <Icon name={Icons.ArrowLeft} />
          <span className="mb-1 text-xs font-bold text-primary-800">
            Back to Customer Profile
          </span>
        </Link>
      </div>

      <AdminTableProperties
        searchQuery={searchQueryString}
        handleSearch={handleChange}
        exportData={[]}
        exportHeader={[]}
        fileName={'payments'}
        showFilter={false}
      />

      <div className="mt-3 flex flex-col overflow-auto rounded-[5px] bg-white">
        <table
          className={clsx({
            'w-full min-w-full': true,
            'flexs flex-col h-[30rem]': table.getRowModel().rows.length === 0
          })}
        >
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr
                key={headerGroup.id}
                className="border-b  text-xs text-[#1F4173]"
              >
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="p-5 text-left font-medium text-[#1F4173]"
                  >
                    {header.isPlaceholder ? null : (
                      <div
                        {...{
                          className: header.column.getCanSort()
                            ? 'cursor-pointer select-none flex'
                            : '',
                          onClick: header.column.getToggleSortingHandler()
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          // asc: (
                          //   <div className="flex flex-row items-center pl-2">
                          //     <Icon name={Icons.Sort} />
                          //   </div>
                          // ),
                          // desc: (
                          //   <div className="flex flex-row items-center pl-2">
                          //     <Icon name={Icons.Sort} />
                          //   </div>
                          // )
                          asc: <></>,
                          desc: <></>
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 && (
              <tr className="mt-10 pt-10 text-center text-xl font-bold">
                <td colSpan={7}>No payment history found</td>
              </tr>
            )}
            {table.getRowModel().rows.map((row, cellIndex) => (
              <tr key={row.id} className="">
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    className={clsx({
                      'p-5 text-xs': true,
                      'bg-[#F9F9F9]': cellIndex % 2 !== 0
                    })}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-5 p-5">
          <Paginator
            numberOfPages={listMetaData.pageCount}
            page={+currentPage}
            hasNext={listMetaData.hasNextPage}
            hasPrevious={listMetaData.hasPreviousPage}
            numberOfItemsPerPage={+numberOfItemsPerPage}
            setNumberOfItemsPerPage={num => {
              // setPageSize(num);
              urlParams.set('items_per_page', num.toString());
              setUrlParams(urlParams);
            }}
            goToNextPage={goToNextPage}
            goToPreviousPage={goToPreviousPage}
          />
        </div>
      </div>
    </div>
  );
}
